import type { VersionedDetail } from '@matter_extractor/shared';

/**
 * What the editor of any versioned object holds while somebody types, and the
 * one comparison that decides whether its only button works (refonte modulaire
 * §4).
 *
 * ## The comparison is the whole point of this file
 *
 * §4: « Enregistrer » is greyed out when the draft is STRUCTURALLY IDENTICAL to
 * the content of the `développement` version, so that a version empty of change
 * cannot exist. Structurally, and not textually: a trailing space, a blank
 * field and a reordering of nothing are not changes, and a screen that enabled
 * its button on a stray space would let somebody write a v6 identical to the
 * v5. The server refuses that too (`no-change`) with the same comparison, which
 * makes this the courtesy and the 409 the guard - but the courtesy is what an
 * operator sees.
 *
 * ## What an object brings, and what it does not
 *
 * A draft's SHAPE belongs to the object - a criterion's is a name, a direction
 * and a numbered list of examples; a persona's is a slider, a presentation and
 * a tone - and so do the two functions that cross between the draft and the
 * wire. Those are the `VersionedDraftKit`. Everything below the kit holds for
 * all four objects and is written once.
 */

/**
 * What the layer needs from an object to run its editor, and nothing more.
 *
 * `Draft` carries a `name` because every versioned object has one (§4: the name
 * lives on the entity and is copied into each version), and the rename of §4 is
 * the one write that does not create a version - the layer commits it on blur
 * and has to read it off the draft to do so.
 */
export interface VersionedDraftKit<Content, DraftContent, Draft extends { readonly name: string }> {
  /**
   * What the editor opens on: the server draft when there is one, the
   * `développement` version otherwise, and an empty editor for an entity that
   * does not exist yet (§4).
   */
  readonly opening: (detail: VersionedDetail<Content, DraftContent> | null) => Draft;
  /** A draft rebuilt from a stored content - what « Repartir de cette version » loads. */
  readonly fromContent: (content: DraftContent) => Draft;
  /** The content a draft would become, or `null` when it could not become one. */
  readonly normalized: (draft: Draft) => Content | null;
  /** What an autosave sends: the same fields, each one omitted when empty. */
  readonly payload: (draft: Draft) => DraftContent;
}

/**
 * Where a NEW entity's entry is kept until its first version, for an object
 * that asks for it (review of !462, issue #148).
 *
 * Before the first save there is no entity, so no server draft (§4, N8), and
 * what is typed lives in the page: a reload loses it. For four objects that is
 * a name and a direction; for the texts of the noyaux it is up to eighteen
 * texts delivered by a specialist a few at a time - the very case of N7. So
 * that object hands the layer these three calls, and the layer makes them only
 * while `detail === null`: the read once, by the initializer of the draft; the
 * write at the rhythm of the autosave; the clear on the first save that
 * landed. An object that passes nothing gets nothing new.
 *
 * Total by contract: a browser that refuses storage answers null, and nothing
 * here may throw - the page has to render the same without it.
 */
export interface VersionedBrowserDraft<Draft> {
  /** What this browser kept, and when it kept it - or null. */
  readonly read: () => { readonly draft: Draft; readonly keptAt: string } | null;
  /** Keep this draft: the instant it was kept, or null when the browser refused. */
  readonly write: (draft: Draft) => string | null;
  /** Forget it: the first save made it a version. */
  readonly clear: () => void;
}

/**
 * What the head says of that browser draft: kept during this visit, restored
 * from an earlier one when the editor opened, or refused by the browser. Null
 * while nothing has been written yet.
 */
export type BrowserDraftStatus =
  | { readonly kind: 'kept'; readonly at: string }
  | { readonly kind: 'restored'; readonly at: string }
  | { readonly kind: 'refused' };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The keys that actually carry something, in no particular order. */
function definedKeys(value: Record<string, unknown>): string[] {
  return Object.keys(value).filter((key) => value[key] !== undefined);
}

/**
 * « Ce contenu est-il le même que celui-là ? » - the comparison of §4, and the
 * SAME walk the server runs in `versioning/compare.ts`.
 *
 * `JSON.stringify(a) === JSON.stringify(b)` is the tempting one-liner and it is
 * wrong here. Key order is decided by whoever built the object - the editor
 * holding `{direction, name}`, Zod re-emitting `{name, direction}` in schema
 * order - so two identical contents would compare different depending on which
 * side wrote last, and the button would arm itself on a round trip that changed
 * nothing.
 *
 * ARRAYS COMPARE IN ORDER, and that is deliberate rather than an omission of
 * set semantics: a criterion's examples are a list the operator ORDERS (they
 * are numbered on the screen), so moving one is a change worth a version.
 *
 * A key whose value is `undefined` counts as ABSENT, on both sides. That is
 * JSON's own reading - the key does not survive a round trip through the column
 * - so treating them differently would make a content stop equalling itself the
 * moment it was stored.
 */
export function structurallyEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((item, index) => structurallyEqual(item, b[index]));
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    const keysOfA = definedKeys(a);
    const keysOfB = definedKeys(b);
    if (keysOfA.length !== keysOfB.length) return false;
    // Keyed lookup rather than two sorted lists: it says « the same keys carry
    // the same values » without deciding an order for keys that have none.
    return keysOfA.every((key) => Object.hasOwn(b, key) && structurallyEqual(a[key], b[key]));
  }

  // Two primitives that failed `===`, or a primitive against a container.
  // `NaN` cannot reach here: it is not a JSON value.
  return false;
}

/**
 * Is « Enregistrer une nouvelle version » usable?
 *
 * Three refusals, and each is a sentence of §4:
 *
 *   - the required fields are not all filled (a new entity's inactive state) -
 *     which is what the object's `normalized` answers `null` for;
 *   - nothing changed since the `développement` version (`no-change`);
 *   - the entity is retired, so it is not editable until it comes back.
 */
export function canSaveVersion<Content, DraftContent, Draft extends { readonly name: string }>(
  draft: Draft,
  detail: VersionedDetail<Content, DraftContent> | null,
  kit: VersionedDraftKit<Content, DraftContent, Draft>,
): boolean {
  const content = kit.normalized(draft);
  if (content === null) return false;
  if (detail === null) return true;
  if (detail.retiredAt !== null) return false;
  return !structurallyEqual(content, detail.developmentContent);
}

/**
 * The number the next save will write.
 *
 * The MAXIMUM of the history plus one, and not `versions.length + 1`: versions
 * are immutable and numbered by the server, so counting rows would be a second
 * implementation of the numbering - and one that answers differently the day a
 * history is served paginated.
 */
export function nextVersionNumber(detail: { versions: { number: number }[] } | null): number {
  if (detail === null) return 1;
  return detail.versions.reduce((highest, version) => Math.max(highest, version.number), 0) + 1;
}
